<template>
  <div>
    <page-header
      :title="title"
      :container-class="containerClass"
      info-text="These default FAQs serve as a template that can be used to create program-specific FAQs."
    />

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Search -->
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent>
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search FAQs"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>
              <div class="col-lg d-flex justify-content-end mt-2 mt-lg-0">
                <b-btn variant="primary" :to="{ name: 'program-faqs-add' }">
                  Add FAQ
                </b-btn>
              </div>
            </div>
          </div>

          <b-table
            :fields="tableColumns"
            :items="paginatedFaqs"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
          >
            <template #cell(question)="data">
              <div class="d-flex align-items-center w-100">
                <span class="mr-2">{{ data.item.question }}</span>
                <b-badge v-if="data.item.is_default" variant="primary">Default</b-badge>
              </div>
            </template>

            <template #cell(answer)="data">
              <span class="d-inline-block w-100">{{ data.item.answer }}</span>
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <router-link
                :to="{ name: 'program-faqs-edit', params: { id: data.item.id } }"
                class="svg-icon mr-2"
                v-b-popover.hover.right
                :title="$t('edit')"
              >
                <i class="material-icons">edit</i>
              </router-link>
              <a
                href="#"
                @click.prevent="deleteFaq(data.item)"
                v-b-popover.hover.right
                :title="$t('delete')"
              >
                <i class="material-icons text-danger">delete</i>
              </a>
            </template>

          </b-table>

          <div class="card-footer" v-if="totalFaqs > perPage">
            <pagination
              v-model="currentPage"
              :total-rows="totalFaqs"
              :per-page="perPage"
              aria-controls="program-faqs-table"
              @change="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '@/common/constants';

export default {
  name: 'ProgramFaqsTable',
  components: { PageHeader, Pagination, MdIcon },
  extends: Page,
  data() {
    return {
      title: 'Default Program FAQs',
      isLoading: false,
      searchTerm: '',
      allFaqs: [],
      currentPage: 1,
      perPage: DEFAULT_PAGE_SIZE,
    };
  },
  computed: {
    // breadcrumb() {
    //   return [
    //     { text: this.$t('home'), to: this.routes.home },
    //     { text: 'Program FAQs', active: true },
    //   ];
    // },
    sortedFaqs() {
      return [...this.allFaqs].sort((a, b) => (a.order_num || 0) - (b.order_num || 0));
    },
    filteredFaqs() {
      if (!this.searchTerm) {
        return this.sortedFaqs;
      }
      const term = this.searchTerm.toLowerCase();
      return this.sortedFaqs.filter(faq => {
        return (
          (faq.question || '').toLowerCase().includes(term) ||
          (faq.answer || '').toLowerCase().includes(term)
        );
      });
    },
    paginatedFaqs() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredFaqs.slice(start, start + this.perPage);
    },
    totalFaqs() {
      return this.filteredFaqs.length;
    },
    tableColumns() {
      return [
        {
          key: 'question',
          label: 'Question',
          thStyle: { width: '45%' },
          tdClass: 'faq-question-column align-middle',
        },
        {
          key: 'answer',
          label: 'Answer',
          thStyle: { width: '45%' },
          tdClass: 'faq-answer-column align-middle',
        },
        {
          key: 'actions',
          label: '',
          tdClass: 'text-right',
          thStyle: { minWidth: '100px' },
        },
      ];
    },
  },
  methods: {
    ...mapActions('program', ['getDefaultProgramFaqs', 'deleteDefaultProgramFaq']),
    async fetchFaqs() {
      this.isLoading = true;
      try {
        const response = await this.getDefaultProgramFaqs({});
        this.allFaqs = response.data || [];
        this.currentPage = 1;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      } finally {
        this.isLoading = false;
      }
    },
    onSearch() {
      this.debouncedFilter(this);
    },
    debouncedFilter: debounce(vm => {
      vm.currentPage = 1;
    }, 300),
    onPageChange(page) {
      this.currentPage = page;
      document.getElementById('app').scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    editFaq(faq) {
      this.$router.push({
        name: 'program-faqs-edit',
        params: { id: faq.id }
      });
    },
    async deleteFaq(faq) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete this FAQ: "${faq.question}"?`,
        {
          title: 'Confirm Deletion',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      );

      if (confirmed) {
        this.isLoading = true;
        try {
          await this.deleteDefaultProgramFaq(faq.id);
          this.makeToast({ variant: 'success', msg: 'FAQ deleted successfully!' });
          await this.fetchFaqs();
        } catch (error) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
  async mounted() {
    this.fetchFaqs();
  },
};
</script>
